
import {
  useTranslate,
  IResourceComponentsProps,
  useApiUrl,
  HttpError,
  CrudFilters,
  LogicalFilter,
  ConditionalFilter,
  getDefaultFilter,
} from '@pankod/refine-core';
import {
  EditButton,
  DeleteButton,
  CreateButton,
  Table,
  TextField,
  Button,
  Popconfirm,
  Space,
} from '@pankod/refine-antd';
import { Card, List as AntdList, Carousel, Image, message } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  LeftOutlined,
  RightOutlined,
  TableOutlined,
  AppstoreOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Ownership } from 'interfaces';
import UserHandler from 'util/UserHandler';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useDelete, useTable } from '@pankod/refine-core';
import { getColumnSearchProps } from 'components/data/customFunc';
import type { Breakpoint } from 'antd/es/_util/responsiveObserve';
import { UserRoleEnum } from 'types/users/userRole';

export const OwnershipList: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const authUser = UserHandler.getInstance().user;
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { tableQueryResult, filters, setFilters } = useTable<
    Ownership,
    HttpError
  >({
    resource: 'ownership',
    initialSorter: [
      {
        field: 'id',
        order: 'desc',
      },
    ],
    initialPageSize: pageSize,
  });

  const { mutate: deleteOwnership } = useDelete();

  // Mapping des valeurs traduites vers les valeurs brutes pour le filtrage
  const statusTranslationMap: { [key: string]: string } = {
    [t('status.pending', { ns: 'ownerships' }).toLowerCase()]: 'pending',
    [t('status.accepted', { ns: 'ownerships' }).toLowerCase()]: 'accepted',
    [t('status.rejected', { ns: 'ownerships' }).toLowerCase()]: 'rejected',
  };

  const setData = useCallback(async () => {
    const tableData = tableQueryResult?.data?.data;
    if (!tableData) return;

    const sortedData = [...tableData].sort((a, b) => b.id - a.id);

    const data = sortedData.map((o: any) => {
      let images: string[] = [];
      if (o.attachments && Array.isArray(o.attachments)) {
        images = o.attachments
          .filter((attachment: any) => {
            if (!attachment?.path) return false;
            const extension = attachment.path.split('.').pop()?.toLowerCase();
            return ['jpg', 'jpeg', 'png', 'gif'].includes(extension);
          })
          .map((attachment: any) => {
            const path = attachment.path.startsWith('http')
              ? attachment.path
              : `${process.env.REACT_APP_SERVER}${attachment.path}`;
            return path;
          });
      }
      images = Array.from(new Set(images));
      if (!images.length) {
        images = [`${process.env.REACT_APP_SERVER}/no_img.jpg`];
      }
      return { ...o, images };
    });
    setDataSource(data);
  }, [tableQueryResult.data]);

  const filteredDataSource = useMemo(() => {
    if (!filters?.length) return dataSource;

    return dataSource.filter((item) =>
      filters.every((filter: LogicalFilter | ConditionalFilter) => {
        if ('field' in filter && filter.value) {
          const value = item[filter.field]?.toString().toLowerCase();
          let filterValue = filter.value?.toString().toLowerCase();

          // Pour validation_status, convertir la valeur de recherche traduite en valeur brute
          if (filter.field === 'validation_status') {
            filterValue = statusTranslationMap[filterValue] || filterValue;
          }

          return value?.includes(filterValue);
        }
        return true;
      }),
    );
  }, [dataSource, filters, statusTranslationMap]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setData();
    }, 0);
    return () => clearTimeout(timer);
  }, [setData]);

  useEffect(() => {
    tableQueryResult.refetch();
  }, [currentPage, tableQueryResult.refetch]);

  useEffect(() => {
    console.log('Filtres actuels:', filters);
  }, [filters]);

  const handleDelete = useCallback(
    (id: number) => {
      deleteOwnership(
        {
          resource: 'ownership',
          id,
        },
        {
          onSuccess: () => {
            message.success(
              t('messages.deleteSuccess', {
                ns: 'ownerships',
              }),
            );
            tableQueryResult.refetch();
          },
          onError: (error) => {
            message.error(
              t('messages.deleteError', {
                ns: 'ownerships',
                defaultValue: `Échec de la suppression : ${error.message}`,
              }),
            );
          },
        },
      );
    },
    [deleteOwnership, t, tableQueryResult],
  );

  const handleResetFilters = useCallback(() => {
    setFilters([], 'replace');
    setCurrentPage(1);
    tableQueryResult.refetch();
    message.success(
      t('messages.resetFilters', {
        ns: 'ownerships',
      }),
    );
  }, [setFilters, tableQueryResult, t]);

  const contentStyle: React.CSSProperties = useMemo(
    () => ({
      margin: 0,
      textAlign: 'center',
    }),
    [],
  );

  const PrevArrow = useCallback((props: any) => {
    const { className, style, onClick } = props;
    return (
      <div
        className={`${className} custom-arrow`}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          background: 'rgba(0, 0, 0, 0.6)',
          borderRadius: '50%',
          left: '8px',
          zIndex: 1,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
        }}
        onClick={onClick}
      >
        <LeftOutlined style={{ color: '#fff', fontSize: '12px' }} />
      </div>
    );
  }, []);

  const NextArrow = useCallback((props: any) => {
    const { className, style, onClick } = props;
    return (
      <div
        className={`${className} custom-arrow`}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          background: 'rgba(0, 0, 0, 0.6)',
          borderRadius: '50%',
          right: '8px',
          zIndex: 1,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
        }}
        onClick={onClick}
      >
        <RightOutlined style={{ color: '#fff', fontSize: '12px' }} />
      </div>
    );
  }, []);

  const columns = [
    {
      dataIndex: 'index',
      key: 'index',
      title: t('ownerships.fields.id', 'ID'),
      render: (_2: any, _1: Ownership, index: number) => (
        <TextField value={(currentPage - 1) * pageSize + index + 1} />
      ),
      responsive: ['md'] as Breakpoint[],
    },
    {
      dataIndex: 'name',
      key: 'name',
      title: t('ownerships.fields.name', 'Nom'),
      render: (value: string) => <TextField value={value} />,
      ...getColumnSearchProps('name', 'text'),
      defaultFilteredValue: getDefaultFilter('name', filters),
    },
    {
      dataIndex: 'address',
      key: 'address',
      title: t('ownerships.fields.address', 'Adresse'),
      render: (value: string) => <TextField value={value} />,
      ...getColumnSearchProps('address', 'text'),
      defaultFilteredValue: getDefaultFilter('address', filters),
    },
    {
      dataIndex: 'city',
      key: 'city',
      title: t('ownerships.fields.city', 'Ville'),
      render: (value: string) => <TextField value={value} />,
      ...getColumnSearchProps('city', 'text'),
      defaultFilteredValue: getDefaultFilter('city', filters),
    },
    {
      dataIndex: 'province',
      key: 'province',
      title: t('ownerships.fields.province', 'Province'),
      render: (value: string) => <TextField value={value} />,
      ...getColumnSearchProps('province', 'text'),
      defaultFilteredValue: getDefaultFilter('province', filters),
    },
    {
      dataIndex: 'price',
      key: 'price',
      title: t('ownerships.fields.price', 'Prix'),
      render: (value: number) => (
        <TextField value={`${value.toLocaleString()} CFA`} />
      ),
    },
    {
      dataIndex: 'owner_name',
      key: 'owner_name',
      title: t('ownerships.fields.owner_name', 'Propriétaire'),
      render: (value: string) => <TextField value={value} />,
      ...getColumnSearchProps('owner_name', 'text'),
      defaultFilteredValue: getDefaultFilter('owner_name', filters),
    },
    {
      dataIndex: 'validation_status',
      key: 'validation_status',
      title: t('ownerships.fields.validation_status', 'Statut de validation'),
      render: (value: string) => (
        <TextField value={t(`status.${value}`, { ns: 'ownerships' })} />
      ),
      ...getColumnSearchProps('validation_status', 'text'),
      defaultFilteredValue: getDefaultFilter('validation_status', filters),
    },
    {
      title: t('ownerships.fields.actions', 'Actions'),
      dataIndex: 'actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 120,
      render: (_2: any, record: Ownership) => (
        <Space size="middle" className="action-buttons">
          <EditButton
            hideText
            size="small"
            recordItemId={record.id}
            icon={<EditOutlined />}
            title={t('buttons.edit', { ns: 'common', defaultValue: 'Modifier' })}
          />
          <Popconfirm
            title={t('messages.deleteConfirm', {
              ns: 'ownerships',
            })}
            onConfirm={() => handleDelete(record.id)}
            okText={t('buttons.confirm', { ns: 'common', defaultValue: 'Oui' })}
            cancelText={t('buttons.cancel', { ns: 'common', defaultValue: 'Non' })}
          >
            <DeleteButton
              hideText
              size="small"
              recordItemId={record.id}
              icon={<DeleteOutlined />}
              title={t('buttons.delete', {
                ns: 'common',
                defaultValue: 'Supprimer',
              })}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <style>{`
        .ownership-list-container {
          padding: 24px;
          background: linear-gradient(135deg, #f0f6ff 0%, #ffffff 100%);
          min-height: 100vh;
          border-radius: 12px;
        }
        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .view-mode-buttons .ant-btn {
          margin-left: 8px;
          border-radius: 6px;
          transition: all 0.3s ease;
        }
        .view-mode-buttons .ant-btn:hover {
          background: #e6f7ff;
          color: #2f54eb;
        }
        .view-mode-buttons .ant-btn.active {
          background: #2f54eb;
          color: #fff;
          border-color: #2f54eb;
        }
        .create-button-container .ant-btn {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 600;
          color: #fff;
          background: linear-gradient(135deg, #2f54eb 0%, #1a91ff 100%);
          border: none;
          border-radius: 8px;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
          transition: all 0.3s ease;
        }
        .create-button-container .ant-btn:hover {
          background: linear-gradient(135deg, #1a91ff 0%, #2f54eb 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
        }
        .create-button-container .anticon {
          margin-right: 6px;
          font-size: 16px;
        }
        .reset-button .ant-btn {
          margin-left: 8px;
          border-radius: 6px;
          transition: all 0.3s ease;
          background: #fff;
          border-color: #ff4d4f;
          color: #ff4d4f;
        }
        .reset-button .ant-btn:hover {
          background: #fff1f0;
          color: #cf1322;
          border-color: #cf1322;
        }
        .ownership-card {
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          background: #ffffff;
          border: 1px solid #dfe6e9;
          transition: all 0.3s ease;
          height: 350px;
          display: flex;
          flex-direction: column;
          contain: layout style paint;
        }
        .ownership-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }
        .ownership-card .ant-card-head {
          background: linear-gradient(135deg, #2f54eb 0%, #1a91ff 100%);
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          padding: 8px 12px;
          min-height: 44px;
          border-bottom: none;
        }
        .ownership-card .ant-card-head-title {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          line-height: 1.3;
          padding: 0;
        }
        .ownership-card .ant-card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 12px;
          overflow: hidden;
        }
        .ownership-card .ant-card-actions {
          border-top: 1px solid #e8e8e8;
          background: #fafafa;
          padding: 8px 0;
          margin-top: auto;
          display: flex;
          justify-content: center;
          z-index: 5;
          position: relative;
        }
        .ownership-card .ant-card-actions > li {
          margin: 0;
          padding: 0 8px;
        }
        .ownership-card .ant-btn {
          border: none;
          background: transparent;
          color: #b2bec3;
          font-size: 16px;
          transition: all 0.3s ease;
        }
        .ownership-card .ant-btn:hover {
          color: #1a91ff;
          transform: scale(1.1);
        }
        .ownership-card .ant-btn.delete-btn:hover {
          color: #ff4d4f;
        }
        .carousel-container {
          height: 150px;
          width: 100%;
          overflow: hidden;
          border-radius: 8px;
          margin-bottom: 8px;
          position: relative;
          contain: layout;
        }
        .carousel-container .ant-carousel {
          height: 150px;
        }
        .carousel-container img {
          width: 100%;
          height: 150px;
          object-fit: cover;
          border-radius: 8px;
        }
        .price-container {
          margin: 8px 0;
          display: flex;
          justify-content: center;
        }
        .price-text {
          font-size: 16px;
          font-weight: 600;
          color: #2f54eb;
          padding: 4px 12px;
          border-radius: 4px;
        }
        .description-text {
          font-size: 12px;
          color: #595959;
          margin-bottom: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .user-list-table {
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          padding: 4px;
          overflow-x: auto;
          font-family: "Segoe UI", Arial, sans-serif;
        }
        .user-list-table .ant-table {
          min-width: 150px;
          border: none;
        }
        .user-list-table .ant-table-thead > tr > th {
          background-color: #1890ff;
          color: #ffffff;
          font-weight: 600;
          border-bottom: none;
          padding: 12px 16px;
        }
        .user-list-table .ant-table-tbody > tr:hover {
          background-color: #f5f7fa;
        }
        .user-list-table .ant-table-tbody > tr > td {
          padding: 12px 16px;
          color: #333;
          font-weight: 500;
        }
        .action-buttons > * {
          border-radius: 50%;
          width: 36px;
          height: 36px;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: background-color 0.25s ease;
          color: #555;
        }
        .action-buttons > *:hover {
          background-color: #e6f7ff;
          color: #1890ff;
        }
        @media (max-width: 768px) {
          .user-list-table .ant-table {
            min-width: 100%;
            font-size: 14px;
          }
          .action-buttons > * {
            width: 32px;
            height: 32px;
          }
        }
        @media (max-width: 480px) {
          .user-list-table .ant-table {
            font-size: 13px;
          }
          .action-buttons > * {
            width: 28px;
            height: 28px;
          }
        }
      `}</style>
      <div className="ownership-list-container">
        <div className="header-container">
          <div className="create-button-container">
            <CreateButton icon={<PlusOutlined />}>
              {t('buttons.create', {
                ns: 'common',
                defaultValue: 'Créer une propriété',
              })}
            </CreateButton>
          </div>
          <div className="view-mode-buttons">
            <Button
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'active' : ''}
            >
              {t('buttons.gridView', { ns: 'common', defaultValue: 'Grille' })}
            </Button>
            <Button
              icon={<TableOutlined />}
              onClick={() => setViewMode('table')}
              className={viewMode === 'table' ? 'active' : ''}
            >
              {t('buttons.tableView', { ns: 'common', defaultValue: 'Tableau' })}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleResetFilters}
              className="reset-button"
            >
              {t('buttons.resetFilters', {
                ns: 'common',
                defaultValue: 'Réinitialiser',
              })}
            </Button>
          </div>
        </div>

        {viewMode === 'grid' ? (
          <AntdList
            key={filteredDataSource.length}
            grid={{
              gutter: 12,
              xs: 1,
              sm: 2,
              md: 3,
              lg: 4,
              xl: 4,
              xxl: 5,
            }}
            dataSource={filteredDataSource}
            renderItem={(item: Ownership & { images: string[] }) => (
              <AntdList.Item key={item.id}>
                <Card
                  className="ownership-card"
                  size="small"
                  title={`${item.name}, ${item.address}, ${item.city}, ${item.province}`}
                  style={contentStyle}
                  actions={[
                    <EditButton
                      key="edit"
                      hideText
                      size="small"
                      recordItemId={item.id}
                      icon={<EditOutlined />}
                    />,
                    <Popconfirm
                      key="delete"
                      title={t('messages.deleteConfirm', {
                        ns: 'ownerships',
                        defaultValue: 'Voulez-vous vraiment supprimer cette propriété ?',
                      })}
                      onConfirm={() => handleDelete(item.id)}
                      okText={t('buttons.confirm', { ns: 'common', defaultValue: 'Oui' })}
                      cancelText={t('buttons.cancel', { ns: 'common', defaultValue: 'Non' })}
                    >
                      <DeleteButton
                        hideText
                        size="small"
                        recordItemId={item.id}
                        icon={<DeleteOutlined />}
                        className="delete-btn"
                      />
                    </Popconfirm>,
                  ]}
                >
                  <div className="carousel-container">
                    <Carousel
                      style={contentStyle}
                      autoplay
                      arrows
                      prevArrow={<PrevArrow />}
                      nextArrow={<NextArrow />}
                      dots={false}
                      autoplaySpeed={4000}
                      speed={500}
                    >
                      {item.images?.map((url: string, index: number) => (
                        <div key={`${item.id}-${index}`}>
                          <Image
                            src={url}
                            width="100%"
                            height={150}
                            fallback={`${process.env.REACT_APP_SERVER}/no_img.jpg`}
                            preview={true}
                            loading="lazy"
                            onError={(e) => {
                              console.error(`Erreur de chargement de l'image: ${url}`);
                              e.currentTarget.src = `${process.env.REACT_APP_SERVER}/no_img.jpg`;
                            }}
                          />
                        </div>
                      ))}
                    </Carousel>
                  </div>
                  <div className="price-container">
                    <span className="price-text">
                      {item.price.toLocaleString()} CFA
                    </span>
                  </div>
                  <div className="description-text">{item.owner_name}</div>
                  <div className="description-text">{item.short_description}</div>
                </Card>
              </AntdList.Item>
            )}
          />
        ) : (
          <Table
            dataSource={filteredDataSource}
            columns={columns}
            rowKey="id"
            className="user-list-table"
            pagination={{
              pageSize,
              current: currentPage,
              total: tableQueryResult?.data?.total,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
            }}
            scroll={{ x: true }}
            onChange={(pagination, tableFilters, sorter) => {
              console.log('Tableau onChange:', { pagination, tableFilters, sorter });
              const newFilters: LogicalFilter[] = Object.entries(tableFilters)
                .reduce<LogicalFilter[]>((acc, [field, value]) => {
                  if (
                    Array.isArray(value) &&
                    value.length > 0 &&
                    value[0] != null
                  ) {
                    let filterValue = String(value[0]);
                    // Convertir la valeur de recherche traduite en valeur brute pour validation_status
                    if (field === 'validation_status') {
                      filterValue = statusTranslationMap[filterValue.toLowerCase()] || filterValue;
                    }
                    acc.push({
                      field,
                      operator: 'contains' as const,
                      value: filterValue,
                    });
                  }
                  return acc;
                }, []);
              setFilters(newFilters, 'replace');
            }}
          />
        )}
      </div>
    </>
  );
};
